/**
 * Quais colunas do papel de trabalho o usuário quer ver.
 *
 * A tabela tem 13 colunas porque o balancete impresso tem 13. Mas nem todo
 * contador confere todas: as 3 de nota só casam em 63% dos itens (o resto é
 * "—"), a Filial é idêntica em toda a conta, e "Valor original" só difere de
 * "Em aberto" quando houve baixa parcial.
 *
 * A escolha é POR USUÁRIO e fica no `localStorage` — é conveniência de leitura,
 * não configuração de negócio: nada aqui muda número, total ou conciliação.
 */

import { COLUNAS_COMPOSICAO, IColunaPapel } from './colunas';

/** Chave do `localStorage`. Versionada: mudar as colunas invalida o salvo. */
export const PREF_COLUNAS = 'composicao:colunas-visiveis:v1';

/**
 * Colunas VISÍVEIS ao abrir a tela pela primeira vez.
 *
 * O critério foi "o que o contador usa para identificar e cobrar um item em
 * aberto", medido no dado real:
 *
 *  - FORA as 3 de nota (Nota fiscal, Série, Data da nota): casam em 63% das
 *    provisões, e em pagamento (PIX, boleto) não existem — a coluna aparece
 *    majoritariamente vazia;
 *  - FORA Filial: é o `external_id` da empresa, IDÊNTICO em todas as linhas da
 *    conta. Uma coluna que nunca varia não informa;
 *  - FORA Valor original: só difere de "Em aberto" em baixa parcial, e a
 *    própria linha já sinaliza "baixa parcial" quando é o caso.
 *
 * Quem precisa delas liga em dois cliques — e a escolha fica salva.
 */
export const COLUNAS_PADRAO: readonly string[] = [
  'Data',
  'Lanç.',
  'Fornecedor',
  'CNPJ',
  'Contrapartida',
  'Em aberto',
  'Dias',
  'Histórico',
];

/**
 * Colunas que NÃO podem ser escondidas.
 *
 * "Em aberto" é a coluna que o TOTAL soma: sem ela o rodapé mostraria um valor
 * sem coluna correspondente, e o alinhamento (derivado de `IDX_EM_ABERTO`)
 * deixaria de fazer sentido. "Data" é o que ordena a tabela — uma lista
 * cronológica sem data vira uma pilha de números.
 */
export const COLUNAS_FIXAS: readonly string[] = ['Data', 'Em aberto'];

const TODAS = COLUNAS_COMPOSICAO.map(c => c.titulo);

/** `true` se a coluna não pode ser desmarcada. */
export const ehFixa = (titulo: string): boolean => COLUNAS_FIXAS.includes(titulo);

/**
 * Lê a preferência salva, ou devolve o padrão.
 *
 * Tolera `localStorage` indisponível (janela privada, SSR) e conteúdo
 * adulterado — uma preferência corrompida não pode deixar a tela em branco.
 * Qualquer coisa que não seja um array de títulos conhecidos volta ao padrão.
 */
export const lerColunas = (): string[] => {
  try {
    const cru = window.localStorage.getItem(PREF_COLUNAS);
    if (!cru) return [...COLUNAS_PADRAO];
    const salvo = JSON.parse(cru);
    if (!Array.isArray(salvo)) return [...COLUNAS_PADRAO];
    // Só títulos que AINDA existem: se uma coluna for removida do código, a
    // preferência antiga não pode ressuscitá-la.
    const validas = salvo.filter(
      (t: unknown): t is string => typeof t === 'string' && TODAS.includes(t),
    );
    // Preferência que esconde tudo deixaria a tabela sem cabeçalho.
    return validas.length ? normalizar(validas) : [...COLUNAS_PADRAO];
  } catch {
    return [...COLUNAS_PADRAO];
  }
};

/** Grava a preferência. Falhar ao gravar não pode quebrar a tela. */
export const gravarColunas = (titulos: string[]): void => {
  try {
    window.localStorage.setItem(PREF_COLUNAS, JSON.stringify(normalizar(titulos)));
  } catch {
    /* conveniência: seguir sem persistir é melhor que estourar */
  }
};

/**
 * Garante as invariantes da seleção: as fixas sempre presentes e a ORDEM
 * sempre a do balancete impresso.
 *
 * A ordem importa: o contador confere contra o papel dele de cima para baixo.
 * Permitir que a seleção reordene as colunas transformaria conferência em caça
 * ao erro — e é por isso que a ordem vem de `COLUNAS_COMPOSICAO`, nunca da
 * ordem em que o usuário clicou.
 */
export const normalizar = (titulos: string[]): string[] => {
  const escolhidas = new Set([...titulos, ...COLUNAS_FIXAS]);
  return TODAS.filter(t => escolhidas.has(t));
};

/**
 * O `title` do item do menu — o que o usuário lê ao passar o mouse.
 *
 * Fica aqui, e não no JSX, porque é decisão de três casos: item desabilitado
 * precisa DIZER por que está desabilitado, senão o clique que não faz nada
 * parece defeito da tela.
 */
export const dicaDaColuna = (titulo: string, marcada: boolean): string => {
  if (ehFixa(titulo)) return 'Esta coluna não pode ser escondida';
  return marcada ? 'Clique para esconder' : 'Clique para mostrar';
};

/**
 * Liga/desliga uma coluna.
 *
 * As fixas são protegidas por `normalizar`, NÃO por uma guarda aqui: ele
 * reinjeta `COLUNAS_FIXAS` em qualquer seleção, então desmarcar "Data" já
 * devolve "Data". Havia uma guarda explícita (`if (ehFixa) return ...`) e ela
 * era redundante — o teste de mutação mostrou que removê-la não muda resultado
 * nenhum. Duas linhas defendendo a mesma regra é pior que uma: dá a impressão
 * de que a regra está garantida nos dois lugares quando o comportamento só
 * depende de um.
 */
export const alternar = (visiveis: string[], titulo: string): string[] => {
  const fora = visiveis.filter(t => t !== titulo);
  return normalizar(fora.length === visiveis.length ? [...visiveis, titulo] : fora);
};

/**
 * A geometria da tabela DERIVADA da seleção do usuário.
 *
 * Tudo o que dependia das 13 colunas fixas passa a depender do que está
 * visível. É a parte que quebra silenciosamente: esconder uma coluna sem
 * recalcular o `colSpan` faz o rodapé somar debaixo da coluna errada, e a
 * linha de conciliação logo abaixo passa a parecer divergência numa conta que
 * fecha — sem erro de tipo, de lint ou de render.
 */
export interface IGeometria {
  /** As colunas efetivamente renderizadas, na ordem do balancete. */
  colunas: IColunaPapel[];
  /** `colSpan` das linhas que atravessam a tabela inteira. */
  total: number;
  /** `colSpan` do rótulo "Total da composição": leva até antes de "Em aberto". */
  rotulo: number;
  /** Colunas à direita do total, para a linha fechar. */
  resto: number;
  /** Soma das larguras visíveis — o `minW` da tabela. */
  larguraMinima: string;
}

export const geometria = (visiveis: string[]): IGeometria => {
  const colunas = COLUNAS_COMPOSICAO.filter(c => visiveis.includes(c.titulo));
  // "Em aberto" é fixa, então o índice existe sempre; o `Math.max` é rede de
  // segurança para não gerar colSpan negativo se alguém mexer nas fixas.
  const idx = Math.max(
    0,
    colunas.findIndex(c => c.titulo === 'Em aberto'),
  );
  return {
    colunas,
    total: colunas.length,
    rotulo: idx,
    resto: Math.max(0, colunas.length - idx - 1),
    larguraMinima: `${colunas.reduce((s, c) => s + parseFloat(c.largura), 0)}px`,
  };
};

/**
 * A escolha de colunas como vai na query do XLSX (`colunas=`).
 *
 * O arquivo baixado é "a visualização que está na tela" (pedido do usuário):
 * o backend monta a aba "Conta analítica" só com estas colunas e acrescenta a
 * aba "Razão". Vai NORMALIZADO — fixas dentro, ordem do balancete — porque é
 * o backend que valida os títulos, e um título fora da ordem ou sem as fixas
 * daria um arquivo diferente da tela. Títulos separados por vírgula: nenhum
 * título tem vírgula.
 */
export const paramColunas = (visiveis: string[]): string => normalizar(visiveis).join(',');
